import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Chip } from '@mui/material';
import { UserCheck, Plus, Trash2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { manageTrackerList } from '../../../redux/affiliateTrackerSlice';
import useToast from '../../../common_components/Toast/useToast';
import { TextInput, EmailInput } from '../../../common_components/FormInputs';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import AppButton from '../../../common_components/AppButton';
import { useOptedOutCheck } from '../../../hooks/useOptedOutCheck';
export default function EditTrackerDrawer({ open, onClose, selectedRow, currentListDetails, statusStyles, onSaveSuccess }) {
    const dispatch = useDispatch();
    const toast = useToast();
    const checkOptedOut = useOptedOutCheck();
    const [drawerContacts, setDrawerContacts] = useState([]);
    const [drawerSaving, setDrawerSaving] = useState(false);
    const [optedOutIndexes, setOptedOutIndexes] = useState(new Set());
    const [optedOutSecondaryIndexes, setOptedOutSecondaryIndexes] = useState(new Set());
    const handleEmailBlur = async (index, email) => {
        if (!email.trim()) {
            setOptedOutIndexes(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
            return;
        }
        const isOut = await checkOptedOut(email.trim());
        setOptedOutIndexes(prev => {
            const next = new Set(prev);
            if (isOut)
                next.add(index);
            else
                next.delete(index);
            return next;
        });
    };
    const handleSecondaryEmailBlur = async (index, email) => {
        if (!email.trim()) {
            setOptedOutSecondaryIndexes(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
            return;
        }
        const isOut = await checkOptedOut(email.trim());
        setOptedOutSecondaryIndexes(prev => {
            const next = new Set(prev);
            if (isOut)
                next.add(index);
            else
                next.delete(index);
            return next;
        });
    };
    useEffect(() => {
        if (open && selectedRow) {
            const primary = {
                id: selectedRow.id,
                first_name: selectedRow.first_name || '',
                last_name: selectedRow.last_name || '',
                primary_email: selectedRow.primary_email || '',
                secondary_email: selectedRow.secondary_email || '',
                job_title: selectedRow.job_title || '',
                status: selectedRow.status || 'Pending'
            };
            const siblings = currentListDetails
                .filter(c => c.parent_id && String(c.parent_id) === String(selectedRow.id))
                .map(sib => ({
                id: sib.id,
                first_name: sib.first_name || '',
                last_name: sib.last_name || '',
                primary_email: sib.primary_email || '',
                secondary_email: sib.secondary_email || '',
                job_title: sib.job_title || '',
                status: sib.status || 'Pending'
            }));
            setDrawerContacts([primary, ...siblings]);
        }
        else {
            setDrawerContacts([]);
            setOptedOutIndexes(new Set());
            setOptedOutSecondaryIndexes(new Set());
        }
    }, [open, selectedRow, currentListDetails]);
    const handleUpdateDrawerContact = (index, field, val) => {
        const updated = [...drawerContacts];
        updated[index] = { ...updated[index], [field]: val };
        setDrawerContacts(updated);
    };
    const handleAddDrawerContact = () => {
        setDrawerContacts([
            ...drawerContacts,
            {
                first_name: '',
                last_name: '',
                primary_email: '',
                secondary_email: '',
                job_title: '',
                status: 'Pending'
            }
        ]);
    };
    const validStatuses = ['Pending', 'Approved', 'Rejected'];
    const sanitizeStatus = (s) => validStatuses.includes(s) ? s : 'Pending';
    const handleSaveDrawerContacts = async () => {
        if (!selectedRow)
            return;
        setDrawerSaving(true);
        try {
            const payload = {
                id: selectedRow.id,
                data: {
                    contacts: drawerContacts.map(c => ({
                        first_name: (c.first_name || '').trim(),
                        last_name: (c.last_name || '').trim(),
                        primary_email: (c.primary_email || '').trim(),
                        secondary_email: (c.secondary_email || '').trim(),
                        job_title: (c.job_title || '').trim(),
                        status: sanitizeStatus(c.status || 'Pending')
                    }))
                }
            };
            await dispatch(manageTrackerList({ action: 'update', payload })).unwrap();
            toast.success('Contacts updated successfully!');
            onSaveSuccess();
            onClose();
        }
        catch (err) {
            toast.error(err || 'Failed to save contacts.');
        }
        finally {
            setDrawerSaving(false);
        }
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, width: "md", title: _jsxs(Typography, { sx: { fontSize: '16px', fontWeight: 600, color: '#101828', display: 'flex', alignItems: 'center', gap: '4px' }, children: ["Edit ", _jsx("span", { style: { color: '#98A2B3' }, children: "\u203A" }), " ", _jsx("span", { style: { color: '#101828' }, children: "Outreach Tracker" })] }), headerActions: _jsxs(AppButton, { onClick: handleSaveDrawerContacts, variant: "contained", disabled: drawerSaving, sx: {
                bgcolor: '#EE2A7B',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 3,
                py: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                    bgcolor: '#D91F6A'
                }
            }, children: [_jsx(UserCheck, { size: 16 }), "Save"] }), children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, p: "8px" }, children: [_jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #EAECF0', p: 3 }, children: [_jsx(Typography, { sx: { fontSize: '14px', fontWeight: 700, color: '#EE2A7B', mb: 2 }, children: "Information" }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start' }, children: [_jsx(Typography, { sx: { width: '120px', fontSize: '13px', fontWeight: 500, color: '#475467' }, children: "Search Term" }), _jsx(Typography, { sx: { width: '20px', fontSize: '13px', color: '#475467' }, children: ":" }), _jsx(Typography, { sx: { flex: 1, fontSize: '13px', color: '#101828', fontWeight: 500 }, children: selectedRow?.search_term || '-' })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start' }, children: [_jsx(Typography, { sx: { width: '120px', fontSize: '13px', fontWeight: 500, color: '#475467' }, children: "Domain" }), _jsx(Typography, { sx: { width: '20px', fontSize: '13px', color: '#475467' }, children: ":" }), _jsx(Typography, { sx: { flex: 1, fontSize: '13px', color: '#101828', fontWeight: 500 }, children: selectedRow?.domain || '-' })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start' }, children: [_jsx(Typography, { sx: { width: '120px', fontSize: '13px', fontWeight: 500, color: '#475467' }, children: "Article" }), _jsx(Typography, { sx: { width: '20px', fontSize: '13px', color: '#475467' }, children: ":" }), _jsx(Typography, { sx: {
                                                flex: 1,
                                                fontSize: '13px',
                                                color: '#026AA2',
                                                fontWeight: 500,
                                                wordBreak: 'break-all',
                                                cursor: 'pointer',
                                                '&:hover': { textDecoration: 'underline' }
                                            }, onClick: () => selectedRow?.article && window.open(selectedRow.article, '_blank'), children: selectedRow?.article || '-' })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center' }, children: [_jsx(Typography, { sx: { width: '120px', fontSize: '13px', fontWeight: 500, color: '#475467' }, children: "Status" }), _jsx(Typography, { sx: { width: '20px', fontSize: '13px', color: '#475467' }, children: ":" }), selectedRow?.status && (_jsx(Chip, { label: statusStyles[selectedRow.status]?.label || selectedRow.status, size: "small", sx: {
                                                height: 22,
                                                borderRadius: '999px',
                                                bgcolor: statusStyles[selectedRow.status]?.bg || '#FFF4ED',
                                                color: statusStyles[selectedRow.status]?.color || '#C2410C',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                            } }))] })] })] }), drawerContacts.map((contact, index) => (_jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #EAECF0', p: 2.5 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: '12px', borderBottom: '1px solid #EAECF0', mb: '20px' }, children: [_jsxs(Typography, { sx: { fontSize: '14px', fontWeight: 600, color: '#EE2A7B' }, children: ["Contact #", index + 1, " ", index === 0 ? '(Primary Contact)' : ''] }), _jsx(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: '8px',
                                        border: '1px solid #EAECF0',
                                        bgcolor: '#fff',
                                        overflow: 'hidden'
                                    }, children: _jsx(IconButton, { onClick: () => {
                                            if (drawerContacts.length === 1 && index === 0) {
                                                setDrawerContacts([{
                                                        first_name: '',
                                                        last_name: '',
                                                        primary_email: '',
                                                        secondary_email: '',
                                                        job_title: '',
                                                        status: 'Pending'
                                                    }]);
                                            }
                                            else {
                                                const updated = drawerContacts.filter((_, i) => i !== index);
                                                setDrawerContacts(updated);
                                            }
                                        }, size: "small", sx: {
                                            width: '34px',
                                            height: '34px',
                                            borderRadius: 0,
                                            color: '#FDA29B',
                                            '&:hover': {
                                                bgcolor: '#F9FAFB',
                                                color: '#D92D20'
                                            }
                                        }, children: _jsx(Trash2, { size: 16 }) }) })] }), _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }, children: [_jsx(Box, { children: _jsx(TextInput, { label: "First Name", value: contact.first_name, onChange: (e) => handleUpdateDrawerContact(index, 'first_name', e.target.value), placeholder: "Enter First Name" }) }), _jsx(Box, { children: _jsx(TextInput, { label: "Last Name", value: contact.last_name, onChange: (e) => handleUpdateDrawerContact(index, 'last_name', e.target.value), placeholder: "Enter Last Name" }) }), _jsxs(Box, { children: [_jsx(Box, { sx: optedOutIndexes.has(index) ? {
                                                '& input': { color: '#F04438 !important', textDecoration: 'line-through' },
                                                '& fieldset': { borderColor: '#F04438 !important' }
                                            } : {}, children: _jsx(EmailInput, { label: "Primary Email", value: contact.primary_email, onChange: (e) => handleUpdateDrawerContact(index, 'primary_email', e.target.value), onBlur: (e) => handleEmailBlur(index, e.target.value), placeholder: "e.g. lorem@gmail.com" }) }), optedOutIndexes.has(index) && (_jsx(Typography, { sx: { fontSize: '10px', color: '#F04438', lineHeight: 1.2, mt: '4px' }, children: "This contact has opted out. You cannot add them." })), (selectedRow?.hubspot_sync_date || selectedRow?.hubspot_contact_id) && contact.primary_email?.trim() !== (selectedRow.primary_email || '').trim() && (_jsx(Typography, { sx: { fontSize: '11px', color: '#B54708', bgcolor: '#FFFAEB', p: '4px 8px', borderRadius: '4px', mt: '4px', border: '1px solid #FEDF89' }, children: "\u26A0\uFE0F Note: Changing this email will reset HubSpot sync so the new email can be synced as a new entry." }))] }), _jsxs(Box, { children: [_jsx(Box, { sx: optedOutSecondaryIndexes.has(index) ? {
                                                '& input': { color: '#F04438 !important', textDecoration: 'line-through' },
                                                '& fieldset': { borderColor: '#F04438 !important' }
                                            } : {}, children: _jsx(EmailInput, { label: "Secondary Email", value: contact.secondary_email, onChange: (e) => handleUpdateDrawerContact(index, 'secondary_email', e.target.value), onBlur: (e) => handleSecondaryEmailBlur(index, e.target.value), placeholder: "e.g. lorem@gmail.com" }) }), optedOutSecondaryIndexes.has(index) && (_jsx(Typography, { sx: { fontSize: '10px', color: '#F04438', lineHeight: 1.2, mt: '4px' }, children: "This contact has opted out. You cannot add them." }))] }), _jsx(Box, { sx: { gridColumn: '1 / -1' }, children: _jsx(TextInput, { label: "Job Title", value: contact.job_title, onChange: (e) => handleUpdateDrawerContact(index, 'job_title', e.target.value), placeholder: "Enter Job Title" }) })] })] }, index))), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mt: 1 }, children: _jsxs(Button, { onClick: handleAddDrawerContact, variant: "outlined", sx: {
                            height: '36px',
                            border: '1px solid #D0D5DD',
                            borderRadius: '8px',
                            bgcolor: '#fff',
                            textTransform: 'none',
                            color: '#344054',
                            fontWeight: 600,
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            px: '12px',
                            '&:hover': {
                                bgcolor: '#F9FAFB',
                                borderColor: '#D0D5DD'
                            }
                        }, children: [_jsx(Plus, { size: 16 }), "Add New Contact"] }) })] }) }));
}
